import { useEffect, useMemo, useRef, useState } from 'react';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { createServiceCenter, updateServiceCenter } from '../../graphql/companyResourceQueries';
import type { ServiceCenter } from '../../types/schema';
import { formatPhoneNumber, handlePhoneChange } from '../../utils/phoneFormat';
import { getInterimDistrictParentId } from '../../utils/regionLocationHierarchy';
import { cleanIdList } from '../../utils/serviceCenterUtils';
import { Btn, BtnIconOnly, IconClose, InputField, MultiSelect } from '../ui';
import type { MultiSelectOption, MultiSelectOptionGroup } from '../ui';
import '../../css/CompanyResourcesPage.css';

interface ServiceCenterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (saved?: ServiceCenter | null) => Promise<void>;
    editServiceCenter: ServiceCenter | null;
    companyId: string;
    /** Create-only coverage. Resources leaves this empty so a new center covers every region and location. */
    initialCoverage?: { regionIds?: string[]; locationIds?: string[] };
}

const countLabel = (count: number, singular: string, plural: string) =>
    count === 1 ? `1 ${singular}` : `${count} ${plural}`;

export const ServiceCenterModal = ({
    isOpen,
    onClose,
    onSave,
    editServiceCenter,
    companyId,
    initialCoverage,
}: ServiceCenterModalProps) => {
    const { companyRegions, companyLocations, companyDistricts } = useRegionAndLocationContext();
    const [name, setName] = useState(editServiceCenter?.name || '');
    const [address, setAddress] = useState(editServiceCenter?.address || '');
    const [phoneNumber, setPhoneNumber] = useState(formatPhoneNumber(editServiceCenter?.phoneNumber || ''));
    const [faxNumber, setFaxNumber] = useState(formatPhoneNumber(editServiceCenter?.faxNumber || ''));
    const [repName, setRepName] = useState(editServiceCenter?.repName || '');
    const [repPhoneNumber, setRepPhoneNumber] = useState(formatPhoneNumber(editServiceCenter?.repPhoneNumber || ''));
    const [regionIds, setRegionIds] = useState<string[]>(() =>
        cleanIdList(editServiceCenter?.regionIds ?? initialCoverage?.regionIds),
    );
    const [locationIds, setLocationIds] = useState<string[]>(() =>
        cleanIdList(editServiceCenter?.locationIds ?? initialCoverage?.locationIds),
    );
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const regionOptions = useMemo<MultiSelectOption[]>(
        () =>
            [...companyRegions]
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((region) => ({ value: region.id, label: region.name || 'Region' })),
        [companyRegions],
    );

    const locationGroups = useMemo<MultiSelectOptionGroup[]>(() => {
        const regionsToShow = regionIds.length
            ? companyRegions.filter((region) => regionIds.includes(region.id))
            : companyRegions;
        const knownRegionIds = new Set(companyRegions.map((region) => region.id));

        const groups = regionsToShow
            .map((region) => {
                const options = companyLocations
                    .filter((location) => location.regionalDivisionId === region.id)
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((location) => {
                        const districtId = getInterimDistrictParentId(location);
                        const districtName = districtId
                            ? companyDistricts.find((district) => district.id === districtId)?.name
                            : '';
                        return {
                            value: location.id,
                            label: [districtName, location.name].filter(Boolean).join(' › ') || 'Location',
                        };
                    });
                return { label: region.name || 'Region', options };
            })
            .filter((group) => group.options.length > 0);

        if (regionIds.length === 0) {
            const otherOptions = companyLocations
                .filter((location) => !knownRegionIds.has(location.regionalDivisionId))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((location) => ({ value: location.id, label: location.name || 'Location' }));
            if (otherOptions.length > 0) {
                groups.push({ label: 'Other', options: otherOptions });
            }
        }

        return groups;
    }, [companyDistricts, companyLocations, companyRegions, regionIds]);

    const locationOptions = useMemo(
        () => locationGroups.flatMap((group) => group.options),
        [locationGroups],
    );

    if (!isOpen) return null;

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!name.trim()) errs.name = 'Service center name is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleRegionChange = (nextRegionIds: string[]) => {
        setRegionIds(nextRegionIds);
        if (nextRegionIds.length === 0) return;
        const allowedLocationIds = new Set(
            companyLocations
                .filter((location) => nextRegionIds.includes(location.regionalDivisionId))
                .map((location) => location.id),
        );
        setLocationIds((current) => current.filter((id) => allowedLocationIds.has(id)));
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const input = {
                companyId,
                name: name.trim(),
                address: address.trim() || null,
                phoneNumber: phoneNumber.trim() || null,
                faxNumber: faxNumber.trim() || null,
                repName: repName.trim() || null,
                repPhoneNumber: repPhoneNumber.trim() || null,
                regionIds,
                locationIds,
            };

            const saved = editServiceCenter
                ? await updateServiceCenter({ id: editServiceCenter.id, ...input })
                : await createServiceCenter(input);
            await onSave(saved);
        } catch (err) {
            console.error('Error saving service center:', err);
            if (isMountedRef.current) setErrors({ _form: 'Failed to save. Please try again.' });
        } finally {
            if (isMountedRef.current) setSaving(false);
        }
    };

    return (
        <div className="cr-modal-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="cr-modal cr-modal--wide cr-modal--service-center" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="service-center-modal-title">
                <div className="cr-modal-header">
                    <h2 id="service-center-modal-title" className="cr-modal-title">
                        {editServiceCenter ? 'Edit Service Center' : 'Add Service Center'}
                    </h2>
                    <BtnIconOnly size="sm" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <div className="cr-modal-body">
                    {errors._form && <div className="cr-error-banner" role="alert">⚠️ {errors._form}</div>}

                    <div className="cr-field">
                        <InputField
                            label="Service Center Name *"
                            inputId="service-center-name"
                            value={name}
                            onChange={setName}
                            hasError={!!errors.name}
                            ariaDescribedBy={errors.name ? 'service-center-name-error' : 'service-center-name-hint'}
                        />
                        {errors.name && <div id="service-center-name-error" className="cr-field-error-text">{errors.name}</div>}
                        <div id="service-center-name-hint" className="cr-field-helper">
                            Address, phone, fax, and representative can be added now or later.
                        </div>
                    </div>

                    <div className="cr-field">
                        <InputField label="Address" inputId="service-center-address" value={address} onChange={setAddress} />
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="Phone Number"
                            inputId="service-center-phone"
                            type="tel"
                            value={phoneNumber}
                            onChange={(value) => handlePhoneChange(value, setPhoneNumber)}
                            placeholder="(xxx) xxx-xxxx"
                        />
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="Fax Number"
                            inputId="service-center-fax"
                            type="tel"
                            value={faxNumber}
                            onChange={(value) => handlePhoneChange(value, setFaxNumber)}
                            placeholder="(xxx) xxx-xxxx"
                        />
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="Representative Name"
                            inputId="service-center-rep"
                            value={repName}
                            onChange={setRepName}
                        />
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="Representative Phone"
                            inputId="service-center-rep-phone"
                            type="tel"
                            value={repPhoneNumber}
                            onChange={(value) => handlePhoneChange(value, setRepPhoneNumber)}
                            placeholder="(xxx) xxx-xxxx"
                        />
                    </div>

                    <div className="cr-field">
                        <span className="cr-field-label" id="service-center-regions-label">Regions</span>
                        <MultiSelect
                            options={regionOptions}
                            value={regionIds}
                            onChange={handleRegionChange}
                            placeholder="All regions"
                            emptyMessage="No regions yet"
                            ariaLabel="Regions this service center covers"
                            ariaDescribedBy="service-center-coverage-hint"
                            formatTriggerLabel={(selected) => {
                                if (selected.length === 0) return 'All regions';
                                if (selected.length === 1) return selected[0].label;
                                return countLabel(selected.length, 'region', 'regions');
                            }}
                        />
                    </div>

                    <div className="cr-field">
                        <span className="cr-field-label" id="service-center-locations-label">Locations</span>
                        <MultiSelect
                            options={locationOptions}
                            optionGroups={locationGroups}
                            value={locationIds}
                            onChange={setLocationIds}
                            placeholder="All locations"
                            emptyMessage={regionIds.length ? 'No locations in the selected regions' : 'No locations yet'}
                            ariaLabel="Locations this service center covers"
                            ariaDescribedBy="service-center-coverage-hint"
                            formatTriggerLabel={(selected) => {
                                if (selected.length === 0) return 'All locations';
                                if (selected.length === 1) return selected[0].label;
                                return countLabel(selected.length, 'location', 'locations');
                            }}
                        />
                        <div className="cr-field-helper" id="service-center-coverage-hint">
                            Choose the regions and locations this service center covers. Leave both empty to cover every region and location.
                        </div>
                    </div>
                </div>
                <div className="cr-modal-footer">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : (editServiceCenter ? 'Update' : 'Create')}
                        buttonOnClick={handleSubmit}
                        buttonDisabled={saving}
                    />
                </div>
            </div>
        </div>
    );
};
